import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMonth = (m) => { const [y, mo] = m.split('-'); return new Date(y, mo - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }); };

const CELL = { padding: '10px 14px', textAlign: 'right', fontSize: 13, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
const HEAD = { padding: '10px 14px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f8fafc', whiteSpace: 'nowrap' };

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState('6');
  const [landlordId, setLandlordId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [expandedProps, setExpandedProps] = useState({});

  useEffect(() => {
    api.get('/employee/landlords').then(res => setLandlords(res.data.landlords || [])).catch(() => {});
    loadReport();
  }, []);

  const loadReport = (overrides = {}) => {
    const m = overrides.months || months;
    const l = overrides.landlordId !== undefined ? overrides.landlordId : landlordId;
    const p = overrides.propertyId !== undefined ? overrides.propertyId : propertyId;
    setLoading(true);
    let url = '/reports/admin?months=' + m;
    if (l) url += '&landlord_id=' + l;
    if (p) url += '&property_id=' + p;
    api.get(url)
      .then(res => { setData(res.data); setExpandedProps({}); })
      .catch(() => toast.error('Could not load report'))
      .finally(() => setLoading(false));
  };

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Property', 'Landlord', 'Month', 'Total Units', 'Active Tenants', 'New Enrollments', 'Penetration %', 'ISP Cost', 'Gross Revenue', 'Net Margin']];
    data.properties.forEach(p => {
      p.monthly.forEach(m => {
        rows.push([p.name, p.landlord_name || '', m.month, m.total_units, m.active_tenants, m.new_enrollments, m.penetration_pct + '%', m.isp_cost, m.gross_revenue, m.net_margin]);
      });
    });
    const csv = rows.map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = 'report_' + new Date().toISOString().split('T')[0] + '.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  const toggleProp = (id) => setExpandedProps(p => ({ ...p, [id]: !p[id] }));

  // Portfolio totals across all months
  const totals = data?.properties?.reduce((acc, p) => {
    const last = p.monthly[p.monthly.length - 1];
    if (last) {
      acc.units += last.total_units;
      acc.active += last.active_tenants;
      acc.revenue += last.gross_revenue;
      acc.isp += last.isp_cost;
    }
    return acc;
  }, { units: 0, active: 0, revenue: 0, isp: 0 });

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, padding: '2rem', maxWidth: 1200 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
          <div>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: 4 }}>Reports</h1>
            <p style={{ color: '#64748b', fontSize: 14 }}>Month-by-month property performance across the portfolio.</p>
          </div>
          <button onClick={exportCSV} disabled={!data} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: !data ? 0.5 : 1 }}>
            ⬇ Export CSV
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, margin: '16px 0 20px', flexWrap: 'wrap' }}>
          <select value={months} onChange={e => { setMonths(e.target.value); loadReport({ months: e.target.value }); }}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: 'white' }}>
            {[3,6,12,24].map(m => <option key={m} value={m}>{m} Months</option>)}
          </select>
          <select value={landlordId} onChange={e => { setLandlordId(e.target.value); setPropertyId(''); loadReport({ landlordId: e.target.value, propertyId: '' }); }}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: 'white', minWidth: 180 }}>
            <option value="">All Landlords</option>
            {landlords.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name}</option>)}
          </select>
          <button onClick={() => loadReport()} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Refresh
          </button>
        </div>

        {/* Portfolio summary */}
        {totals && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
            {[
              { label: 'Properties', value: data?.properties?.length || 0 },
              { label: 'Total Units', value: totals.units },
              { label: 'Active Tenants', value: totals.active, color: '#16a34a' },
              { label: 'Penetration', value: totals.units > 0 ? Math.round(totals.active / totals.units * 100) + '%' : '0%', color: '#2563eb' },
              { label: 'Revenue MTD', value: '$' + fmt(totals.revenue), color: '#16a34a' },
            ].map((s, i) => (
              <div key={i} style={{ background: 'white', borderRadius: 12, padding: '1rem 1.2rem', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: s.color || '#1e293b' }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Generating report...</div>
        ) : !data || !data.properties.length ? (
          <div style={{ background: 'white', borderRadius: 12, padding: '3rem', textAlign: 'center', color: '#94a3b8', border: '1px solid #e2e8f0' }}>
            No properties found for the selected filters.
          </div>
        ) : (
          data.properties.map(prop => (
            <div key={prop.id} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', marginBottom: 20, overflow: 'hidden' }}>
              {/* Property header */}
              <div onClick={() => toggleProp(prop.id)}
                style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{prop.name}</span>
                  <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 12 }}>{prop.address}</span>
                  {prop.landlord_name && <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>· {prop.landlord_name}</span>}
                </div>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{prop.unit_count}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>Units</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#16a34a' }}>{prop.monthly[prop.monthly.length - 1]?.active_tenants || 0}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>Active</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#2563eb' }}>{prop.monthly[prop.monthly.length - 1]?.penetration_pct || 0}%</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>Penetration</div>
                  </div>
                  <span style={{ color: '#94a3b8', fontSize: 18 }}>{expandedProps[prop.id] ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Monthly table */}
              {expandedProps[prop.id] && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...HEAD, textAlign: 'left' }}>Month</th>
                        <th style={HEAD}>Total Units</th>
                        <th style={HEAD}>Active</th>
                        <th style={HEAD}>New Enrollments</th>
                        <th style={HEAD}>Penetration</th>
                        <th style={HEAD}>ISP Cost</th>
                        <th style={HEAD}>Gross Revenue</th>
                        <th style={HEAD}>Net Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prop.monthly.map((m, i) => (
                        <tr key={m.month} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                          <td style={{ ...CELL, textAlign: 'left', fontWeight: 600 }}>{fmtMonth(m.month)}</td>
                          <td style={CELL}>{m.total_units}</td>
                          <td style={{ ...CELL, color: '#16a34a', fontWeight: 600 }}>{m.active_tenants}</td>
                          <td style={{ ...CELL, color: m.new_enrollments > 0 ? '#2563eb' : '#94a3b8' }}>{m.new_enrollments > 0 ? '+' + m.new_enrollments : '—'}</td>
                          <td style={{ ...CELL, fontWeight: 600, color: m.penetration_pct >= 70 ? '#16a34a' : m.penetration_pct >= 40 ? '#f59e0b' : '#dc2626' }}>
                            {m.penetration_pct}%
                          </td>
                          <td style={{ ...CELL, color: '#dc2626' }}>{m.isp_cost > 0 ? '$' + fmt(m.isp_cost) : '—'}</td>
                          <td style={{ ...CELL, color: '#16a34a', fontWeight: 600 }}>{m.gross_revenue > 0 ? '$' + fmt(m.gross_revenue) : '—'}</td>
                          <td style={{ ...CELL, color: m.net_margin > 0 ? '#16a34a' : m.net_margin < 0 ? '#dc2626' : '#94a3b8', fontWeight: 600 }}>
                            {m.net_margin !== 0 ? (m.net_margin > 0 ? '+' : '') + '$' + fmt(m.net_margin) : '—'}
                          </td>
                        </tr>
                      ))}
                      {/* Totals row */}
                      <tr style={{ background: '#f0f9ff', fontWeight: 700 }}>
                        <td style={{ ...CELL, textAlign: 'left', color: '#2563eb' }}>TOTAL</td>
                        <td style={CELL}>—</td>
                        <td style={CELL}>—</td>
                        <td style={{ ...CELL, color: '#2563eb' }}>+{prop.monthly.reduce((s, m) => s + (m.new_enrollments || 0), 0)}</td>
                        <td style={CELL}>—</td>
                        <td style={{ ...CELL, color: '#dc2626' }}>${fmt(prop.monthly.reduce((s, m) => s + (m.isp_cost || 0), 0))}</td>
                        <td style={{ ...CELL, color: '#16a34a' }}>${fmt(prop.monthly.reduce((s, m) => s + (m.gross_revenue || 0), 0))}</td>
                        <td style={{ ...CELL, color: '#16a34a' }}>${fmt(prop.monthly.reduce((s, m) => s + (m.net_margin || 0), 0))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))
        )}
        <LegalFooter />
      </div>
    </div>
  );
}
